import { useAtomValue } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useCallback } from "react";
import { useQuery } from "react-query";
import {
  atomStore,
  recusAuthStateAtom,
  setSelectedMemberId,
  VendorAuthState,
  vendorAccountsAtom,
} from "../vendor";
import { dateToShortTime } from "../lib";

const REC_API_BASE = "https://api.rec.us/v1";
const REC_FIREBASE_API_KEY = "AIzaSyCp6DCwnx-6GwkMyI2G1b8ixYs4AXZc-7s";
const REC_STRIPE_PUBLISHABLE_KEY =
  "pk_live_51MPUx4CMyY4UUjhBlgalg5uPiGdXHOWbOTEOioIXfReEeAuLviTRXhdTGvZtTnYtDm2eZonv8buTf73YKIzJHV4i00YikF7WiB";

const YBG_ORG_ID = "b026a7c3-1e7e-48e1-9f0a-b2f7154b117d";
const YBG_FIGURE_SKATING_ACTIVITY_ID =
  "f171289a-7e4d-4cb2-84f6-fa628ecc0343";

export function useSelectedRecusMemberId() {
  const accounts = useAtomValue(vendorAccountsAtom);
  const authState = useAtomValue(recusAuthStateAtom);
  const stored = accounts.recus?.selectedMemberId ?? null;
  const memberId = stored || authState?.userId || null;
  const setMemberId = useCallback((id: string | null) => {
    setSelectedMemberId("recus", id);
  }, []);
  return [memberId, setMemberId] as const;
}

const RECENT_BOOKINGS_LIMIT = 7;
export const recusRecentBookingsAtom = atomWithStorage<string[]>(
  "recusRecentBookings",
  [],
);

export function recordRecentBooking(startsAt: Date): void {
  const list = atomStore.get(recusRecentBookingsAtom);
  const t = dateToShortTime(startsAt);
  atomStore.set(
    recusRecentBookingsAtom,
    [t, ...list].slice(0, RECENT_BOOKINGS_LIMIT),
  );
}

export function useRecusHouseholdMembers() {
  const authState = useAtomValue(recusAuthStateAtom);
  return useQuery(
    ["householdMembers", "recus", authState?.userId],
    () => listHouseholdMembers(authState!),
    { enabled: !!authState }
  );
}

export type RecusAuthState = VendorAuthState & {
  idToken: string;
  refreshToken: string;
  firebaseUid: string;
  userId: string;
  householdId: string;
};

export type RecUser = {
  id: string;
  householdId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
};

export type HouseholdMember = {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  type: string;
  isHouseholdOwner: boolean;
};

export type Session = {
  id: string;
  sectionId: string;
  sectionName: string;
  locationId: string | null;
  startsAt: Date;
  endsAt: Date;
  capacity: number;
  participantCount: number;
  pendingParticipantCount: number;
  priceCents: number;
};

export type PaymentMethod = {
  id: string;
  stripePaymentMethodId: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
};

export type BookedSession = {
  bookingId: string;
  sessionId: string;
  sectionId: string;
  sectionName: string;
  startsAt: Date;
  endsAt: Date;
  status: string;
};

export type CartOrder = {
  orderId: string;
  orderItemId: string;
  subtotalCents: number;
  totalCents: number;
  expiresAt: Date;
  stripePaymentMethodId: string | null;
};

export type BookResult = {
  bookingId: string;
  orderId: string;
  paymentIntentId: string;
  amountChargedCents: number;
  bookedSessions: BookedSession[];
};

async function recFetch<T>(
  path: string,
  idToken: string,
  init: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    accept: "application/json",
    authorization: `Bearer ${idToken}`,
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (init.body != null && !headers["content-type"]) {
    headers["content-type"] = "application/json";
  }
  const res = await fetch(`${REC_API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`rec.us ${res.status} ${path}: ${text.slice(0, 200)}`);
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

async function getCurrentPendingCartItemIds(
  auth: RecusAuthState
): Promise<string[]> {
  const body = await recFetch<{
    data: Array<{
      id: string;
      status: string;
      items: Array<{ id: string }>;
    }>;
  }>(`/orders/current?`, auth.idToken);
  const pending = body.data.find((o) => o.status === "pending");
  return pending?.items.map((it) => it.id) ?? [];
}

async function deleteOrderItem(
  auth: RecusAuthState,
  itemId: string
): Promise<void> {
  await recFetch<null>(`/order-items/${itemId}`, auth.idToken, {
    method: "DELETE",
  });
}

async function firebaseProxy<T>(
  url: string,
  body: unknown,
  contentType: string
): Promise<T> {
  const res = await fetch("/recus_proxy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url, method: "POST", body, contentType }),
  });
  if (!res.ok) throw new Error(`recus_proxy ${res.status}`);
  const wrapped = (await res.json()) as { status: number; body: T };
  if (wrapped.status < 200 || wrapped.status >= 300) {
    throw new Error(`firebase ${wrapped.status}`);
  }
  return wrapped.body;
}

type FirebaseSignInResponse = {
  idToken: string;
  refreshToken: string;
  localId: string;
  expiresIn: string;
};

async function firebaseSignIn(
  email: string,
  password: string
): Promise<FirebaseSignInResponse> {
  return firebaseProxy<FirebaseSignInResponse>(
    `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${REC_FIREBASE_API_KEY}`,
    { returnSecureToken: true, email, password, clientType: "CLIENT_TYPE_WEB" },
    "application/json"
  );
}

export async function refreshAuth(refreshToken: string): Promise<{
  idToken: string;
  refreshToken: string;
  firebaseUid: string;
  expiresAt: Date;
}> {
  const body = await firebaseProxy<{
    id_token: string;
    refresh_token: string;
    user_id: string;
    expires_in: string;
  }>(
    `https://securetoken.googleapis.com/v1/token?key=${REC_FIREBASE_API_KEY}`,
    new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: refreshToken,
    }).toString(),
    "application/x-www-form-urlencoded"
  );
  return {
    idToken: body.id_token,
    refreshToken: body.refresh_token,
    firebaseUid: body.user_id,
    expiresAt: new Date(Date.now() + Number(body.expires_in) * 1000),
  };
}

async function fetchCurrentUser(idToken: string): Promise<RecUser> {
  const body = await recFetch<{
    id: string;
    householdId: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string | null;
  }>(`/users/me`, idToken);
  return {
    id: body.id,
    householdId: body.householdId,
    firstName: body.firstName,
    lastName: body.lastName,
    email: body.email,
    phone: body.phone,
  };
}

export async function recusLogin(
  email: string,
  password: string
): Promise<RecusAuthState> {
  const fb = await firebaseSignIn(email, password);
  const user = await fetchCurrentUser(fb.idToken);
  return {
    user: {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
    },
    authedAt: new Date(),
    expiresAt: new Date(Date.now() + Number(fb.expiresIn) * 1000),
    idToken: fb.idToken,
    refreshToken: fb.refreshToken,
    firebaseUid: fb.localId,
    userId: user.id,
    householdId: user.householdId,
  };
}

export async function listHouseholdMembers(
  auth: RecusAuthState
): Promise<HouseholdMember[]> {
  const body = await recFetch<
    Array<{
      id: string;
      firstName: string;
      lastName: string;
      email: string | null;
      type: string;
      isHouseholdOwner: boolean;
    }>
  >(`/users/household-members`, auth.idToken);
  return body.map((m) => ({
    id: m.id,
    firstName: m.firstName,
    lastName: m.lastName,
    email: m.email,
    type: m.type,
    isHouseholdOwner: m.isHouseholdOwner,
  }));
}

type ProgramsResponse = {
  data: Array<{
    id: string;
    name: string;
    sections: Array<{ id: string; name: string }>;
  }>;
};

type SectionSessionsResponse = {
  data: { section: { id: string; name: string; capacity: number } };
  included: {
    sessions: Array<{
      id: string;
      sectionId: string;
      locationId: string | null;
      startsAt: string;
      endsAt: string;
      capacity: number;
      participantCount: number;
      pendingParticipantCount: number;
      pricingPolicy: { default: { cents: number } } | null;
      canceledAt: string | null;
    }>;
  };
};

export async function listUpcomingFreestyleSessions(
  auth: RecusAuthState,
  opts: { fromDate?: Date; toDate?: Date } = {}
): Promise<Session[]> {
  const programs = await recFetch<ProgramsResponse>(
    `/programs?organizationId=${YBG_ORG_ID}&filters[activityIds][in][]=${YBG_FIGURE_SKATING_ACTIVITY_ID}&pg[num]=1&pg[size]=30`,
    auth.idToken
  );

  // The figure-skating activity also contains off-ice classes and lesson
  // requests. Only freestyle programs belong in this per-session schedule.
  const sections = programs.data
    .filter((p) => /\bfreestyle\b/i.test(p.name))
    .flatMap((p) => p.sections);

  const from = opts.fromDate ?? new Date();
  const to = opts.toDate ?? new Date(from.getTime() + 31 * 24 * 60 * 60 * 1000);

  const perSection = await Promise.all(
    sections.map((s) =>
      recFetch<SectionSessionsResponse>(
        `/sections/${s.id}?include[]=sessions`,
        auth.idToken
      )
    )
  );

  const result: Session[] = [];
  for (const resp of perSection) {
    const sectionName = resp.data.section.name;
    for (const s of resp.included.sessions) {
      if (s.canceledAt) continue;
      const startsAt = new Date(s.startsAt);
      if (startsAt < from || startsAt > to) continue;
      const priceCents = s.pricingPolicy?.default?.cents;
      if (typeof priceCents !== "number" || !Number.isFinite(priceCents)) {
        throw new Error(`Missing session price for ${sectionName}`);
      }
      result.push({
        id: s.id,
        sectionId: s.sectionId,
        sectionName,
        locationId: s.locationId,
        startsAt,
        endsAt: new Date(s.endsAt),
        capacity: s.capacity,
        participantCount: s.participantCount,
        pendingParticipantCount: s.pendingParticipantCount,
        priceCents,
      });
    }
  }
  return result.sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime());
}

export async function listPaymentMethods(
  auth: RecusAuthState
): Promise<PaymentMethod[]> {
  const body = await recFetch<{
    data: Array<{
      id: string;
      referenceId: string;
      displayData: {
        brand: string;
        last4: string;
        expMonth: number;
        expYear: number;
      };
      archivedAt: string | null;
    }>;
  }>(
    `/users/${auth.userId}/payment-methods?organizationId=${YBG_ORG_ID}`,
    auth.idToken
  );
  return body.data
    .filter((pm) => !pm.archivedAt)
    .map((pm) => ({
      id: pm.id,
      stripePaymentMethodId: pm.referenceId,
      brand: pm.displayData.brand,
      last4: pm.displayData.last4,
      expMonth: pm.displayData.expMonth,
      expYear: pm.displayData.expYear,
    }));
}

export async function listBookedSessions(
  auth: RecusAuthState,
  memberUserId: string
): Promise<BookedSession[]> {
  const body = await recFetch<{
    data: Array<{
      id: string;
      sessionId: string;
      status: string;
    }>;
    included: {
      sessions: Array<{
        id: string;
        sectionId: string;
        startsAt: string;
        endsAt: string;
      }>;
      sections: Array<{ id: string; name: string }>;
    };
  }>(
    `/users/${memberUserId}/bookings?bookingTypes[]=session&bookingStatuses[]=confirmed&shouldRemoveCanceled=true&pg[num]=1&pg[size]=100`,
    auth.idToken
  );

  const sessionsById = new Map(body.included.sessions.map((s) => [s.id, s]));
  const sectionNameById = new Map(
    body.included.sections.map((s) => [s.id, s.name])
  );

  return body.data.flatMap((b) => {
    const s = sessionsById.get(b.sessionId);
    if (!s) return [];
    return [
      {
        bookingId: b.id,
        sessionId: s.id,
        sectionId: s.sectionId,
        sectionName: sectionNameById.get(s.sectionId) ?? "",
        startsAt: new Date(s.startsAt),
        endsAt: new Date(s.endsAt),
        status: b.status,
      },
    ];
  });
}

async function addToCart(
  auth: RecusAuthState,
  sessionId: string,
  memberUserId: string
): Promise<CartOrder> {
  const body = await recFetch<{
    data: {
      id: string;
      subtotal: number;
      total: number;
      expiresAt: string;
      items: Array<{ id: string }>;
    };
  }>(`/sessions/${sessionId}/participants`, auth.idToken, {
    method: "POST",
    body: JSON.stringify({
      data: {
        participantUserId: memberUserId,
        overrideValidations: false,
        eligibilityOverrides: {},
        selectedAddons: [],
        forms: [],
      },
    }),
  });
  return {
    orderId: body.data.id,
    orderItemId: body.data.items[0].id,
    subtotalCents: body.data.subtotal,
    totalCents: body.data.total,
    expiresAt: new Date(body.data.expiresAt),
    stripePaymentMethodId: null,
  };
}

async function payOrder(
  auth: RecusAuthState,
  orderId: string,
  orderItemId: string,
  amountCents: number
): Promise<{
  paymentIntentId: string;
  clientSecret: string;
  stripePaymentMethodId: string | null;
  amountChargedCents: number;
}> {
  const body = await recFetch<{
    included: {
      payments: Array<{
        gatewayData?: {
          paymentIntentId: string;
          clientSecret: string;
          paymentMethods?: Array<{ id: string }>;
        };
        amount: number;
      }>;
    };
  }>(`/orders/${orderId}/pay`, auth.idToken, {
    method: "POST",
    body: JSON.stringify({
      data: {
        payments: [{ paymentMethodType: "card-online", amountCents }],
        distributions: { [orderItemId]: amountCents },
        source: "retail",
      },
    }),
  });
  const p = body.included.payments[0];
  if (!p?.gatewayData?.paymentIntentId || !p.gatewayData.clientSecret) {
    throw new Error("rec.us payment response missing Stripe confirmation details");
  }
  return {
    paymentIntentId: p.gatewayData.paymentIntentId,
    clientSecret: p.gatewayData.clientSecret,
    stripePaymentMethodId: p.gatewayData.paymentMethods?.[0]?.id ?? null,
    amountChargedCents: p.amount,
  };
}

async function confirmStripePayment(
  paymentIntentId: string,
  clientSecret: string,
  stripePaymentMethodId: string
): Promise<{ status: string }> {
  const params = new URLSearchParams({
    client_secret: clientSecret,
    payment_method: stripePaymentMethodId,
    key: REC_STRIPE_PUBLISHABLE_KEY,
    use_stripe_sdk: "true",
  });
  const res = await fetch(
    `https://api.stripe.com/v1/payment_intents/${paymentIntentId}/confirm`,
    {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: params.toString(),
    }
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`stripe confirm ${res.status}: ${text.slice(0, 200)}`);
  }
  const body = (await res.json()) as { status: string };
  return { status: body.status };
}

export async function bookSession(
  auth: RecusAuthState,
  sessionId: string,
  stripePaymentMethodId: string,
  memberUserId: string
): Promise<BookResult> {
  const existingItemIds = await getCurrentPendingCartItemIds(auth);
  if (existingItemIds.length > 0) {
    await Promise.all(existingItemIds.map((id) => deleteOrderItem(auth, id)));
  }
  const cart = await addToCart(auth, sessionId, memberUserId);

  const payment = await payOrder(
    auth,
    cart.orderId,
    cart.orderItemId,
    cart.subtotalCents
  );
  const stripePm = payment.stripePaymentMethodId ?? stripePaymentMethodId;
  const confirmation = await confirmStripePayment(
    payment.paymentIntentId,
    payment.clientSecret,
    stripePm
  );
  if (confirmation.status !== "succeeded") {
    throw new Error(
      `Payment status: ${confirmation.status}. Check rec.us before booking again.`
    );
  }

  const confirmationError = new Error(
    "Payment succeeded, but the booking could not be confirmed. Check rec.us before booking again."
  );
  for (let attempt = 0; attempt < 6; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) => window.setTimeout(resolve, 1000));
    }
    const bookedSessions = await listBookedSessions(auth, memberUserId).catch(
      () => {
        throw confirmationError;
      }
    );
    const booking = bookedSessions.find((b) => b.sessionId === sessionId);
    if (booking) {
      return {
        bookingId: booking.bookingId,
        orderId: cart.orderId,
        paymentIntentId: payment.paymentIntentId,
        amountChargedCents: payment.amountChargedCents,
        bookedSessions,
      };
    }
  }
  throw confirmationError;
}
